/**
 * Path-to-page-title mapping for document title.
 * Order matters: more specific paths must come first (longer path first).
 * Match: pathname === path OR pathname.startsWith(path + "/")
 */

export const PAGE_TITLE_SITE_SUFFIX = "Natera's Patient Portal";

export interface PageTitleEntry {
  path: string;
  messageId: string;
}

export const pageTitleConfig: PageTitleEntry[] = [
  { path: "/education/prenatal", messageId: "pageTitlePatientEducation" },
  {
    path: "/billing_history_payment_plans",
    messageId: "pageTitlePaymentPlans",
  },
  { path: "/billing_history_statements", messageId: "pageTitleStatements" },
  { path: "/billing_history_payments", messageId: "pageTitlePaymentHistory" },
  { path: "/billing_payment_receipt", messageId: "pageTitlePaymentReceipt" },
  {
    path: "/billing_payment_plan_detail",
    messageId: "pageTitlePaymentPlanDetail",
  },
  { path: "/billing_update_payment", messageId: "pageTitleUpdatePayment" },
  { path: "/billing_payment_success", messageId: "pageTitlePaymentSuccess" },
  { path: "/billing_center_history", messageId: "pageTitleBillingHistory" },
  { path: "/billing_center_help", messageId: "pageTitleBillingHelp" },
  { path: "/billing_review", messageId: "pageTitleReviewBills" },
  { path: "/billing_payment", messageId: "pageTitlePayBill" },
  { path: "/billing_confirmation", messageId: "pageTitlePaymentConfirmation" },
  { path: "/billing_center", messageId: "pageTitleBillingCenter" },
  { path: "/billing_guest_login", messageId: "pageTitleBillingGuestLogin" },
  { path: "/payment", messageId: "pageTitlePayment" },
  {
    path: "/communication/update",
    messageId: "pageTitleCommunicationPreferences",
  },
  { path: "/communication", messageId: "pageTitleCommunicationPreferences" },
  { path: "/contact_details", messageId: "pageTitleContactDetails" },
  { path: "/personal_information", messageId: "pageTitlePersonalInformation" },
  { path: "/security_settings", messageId: "pageTitleSecuritySettings" },
  { path: "/change_password", messageId: "pageTitleChangePassword" },
  { path: "/edit_mfa", messageId: "pageTitleEditMfa" },
  { path: "/update_email", messageId: "pageTitleUpdateEmail" },
  { path: "/resend_update_email", messageId: "pageTitleResendUpdateEmail" },
  { path: "/update_email_error", messageId: "pageTitleUpdateEmailError" },
  { path: "/schedule_blood_draw", messageId: "pageTitleScheduleBloodDraw" },
  { path: "/services/draw_request", messageId: "pageTitleDrawRequest" },
  { path: "/display_video", messageId: "pageTitleVideo" },
  { path: "/test_history", messageId: "pageTitleTestHistory" },
  { path: "/sample_draw", messageId: "pageTitleSampleDraw" },
  { path: "/simple_order", messageId: "pageTitleSimpleOrder" },
  { path: "/test", messageId: "pageTitleTest" },
  { path: "/billing_updates", messageId: "pageTitleBillingUpdates" },
  { path: "/billing_estimate", messageId: "pageTitleBillingEstimate" },
  { path: "/non_covered_service", messageId: "pageTitleNonCoveredService" },
  { path: "/out_of_network", messageId: "pageTitleOutOfNetwork" },
  { path: "/prior_authorization", messageId: "pageTitlePriorAuthorization" },
  { path: "/no_longer_available", messageId: "pageTitleNoLongerAvailable" },
  {
    path: "/insurance_no_longer_available",
    messageId: "pageTitleInsuranceNoLongerAvailable",
  },
  { path: "/guest_login", messageId: "pageTitleGuestLogin" },
  { path: "/update_payment_method", messageId: "pageTitleUpdatePaymentMethod" },
  { path: "/receipt", messageId: "pageTitleReceipt" },
  { path: "/review_invoice", messageId: "pageTitleReviewInvoice" },
  { path: "/create_payment_method", messageId: "pageTitleCreatePaymentMethod" },
  {
    path: "/payment_confirmation",
    messageId: "pageTitleUpsPaymentConfirmation",
  },
  { path: "/payment_failure", messageId: "pageTitlePaymentFailure" },
  { path: "/payment_not_available", messageId: "pageTitlePaymentNotAvailable" },
  { path: "/ups_guest_login", messageId: "pageTitleUpsGuestLogin" },
  { path: "/billing_failure", messageId: "pageTitleBillingFailure" },
  { path: "/setup_profile", messageId: "pageTitleSetupProfile" },
  { path: "/confirmation", messageId: "pageTitleConfirmation" },
  { path: "/profile", messageId: "pageTitleMyAccount" },
  { path: "/home", messageId: "pageTitleHome" },
  { path: "/set_password", messageId: "pageTitleSetPassword" },
  { path: "/send_email", messageId: "pageTitleSendEmail" },
  { path: "/reset_password", messageId: "pageTitleResetPassword" },
  { path: "/reset_password_error", messageId: "pageTitleResetPasswordError" },
  { path: "/unlock", messageId: "pageTitleUnlock" },
  { path: "/activation", messageId: "pageTitleActivation" },
  { path: "/sign_up", messageId: "pageTitleSignUp" },
  { path: "/log_in", messageId: "pageTitleLogIn" },
  { path: "/log_in_redirect", messageId: "pageTitleLogIn" },
  { path: "/", messageId: "pageTitleHome" },
].sort((a, b) => b.path.length - a.path.length);

const TEST_SUPPLEMENTAL_VIDEO_PATH =
  /^\/test\/[^/]+\/[^/]+\/video(?:\/[^/]*)?$/;

const normalizePath = (path: string): string => path.replace(/\/$/, "") || "/";

export const getPageTitleMessageId = (pathname: string): string | null => {
  const normalized = normalizePath(pathname);

  if (TEST_SUPPLEMENTAL_VIDEO_PATH.test(normalized)) {
    return "pageTitleVideo";
  }

  const match = pageTitleConfig.find(({ path }) => {
    const pathNorm = normalizePath(path);
    return (
      normalized === pathNorm ||
      (pathNorm !== "/" && normalized.startsWith(`${pathNorm}/`))
    );
  });

  return match?.messageId ?? null;
};
